import * as React from 'react';
import moment, { Moment } from 'moment';
import classNames from 'classnames';
import { Button } from 'src/shared/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from 'src/shared/components/ui/popover';
import { Calendar } from 'src/shared/components/ui/calendar';
import { InputLabel } from 'src/shared/components/ui/input-label';
import { Calendar as CalendarIcon } from 'lucide-react';
import styles from './BasicDatePicker.module.scss';

export type BasicDatePickerProps = {
    label?: string;
    value: Date | null;
    disablePast?: boolean;
    disableFuture?: boolean;
    format?: string;
    minDate?: Moment;
    hasError?: boolean;
    onChange: (value: Date | null) => void;
    disabled?: boolean;
    /**
     * Distinguishes this picker from any other on the page.
     *
     * Two pickers sharing one id give a document two elements with the same `id`, and a label
     * whose `htmlFor` reaches whichever came first — so clicking the second one's label opens
     * the first. A date range is two pickers side by side, which is where this shows.
     */
    id?: string;
    testId?: string;
    /** For a form whose other labels are styled its own way. */
    labelClassName?: string;
};

const BasicDatePicker = (props: BasicDatePickerProps) => {
    const {
        label,
        value,
        onChange,
        disablePast,
        disableFuture,
        minDate,
        hasError,
        format = 'DD/MM/YYYY',
        disabled = false,
        id = 'date-picker',
        testId,
        labelClassName,
    } = props;

    const className = classNames(styles.basicDatePicker, {
        [styles.basicDatePickerError]: hasError,
    });

    const [open, setOpen] = React.useState(false);

    const today = moment().startOf('day');

    const formatDisplay = (d: Date | null, fmt: string) => {
        if (!d) {
            return '';
        }
        return moment(d).format(fmt);
    };

    const disabledDays = [] as Array<any>;
    if (minDate) {
        disabledDays.push({ before: minDate.toDate() });
    }
    if (disablePast) {
        disabledDays.push({ before: today.toDate() });
    }
    if (disableFuture) {
        disabledDays.push({ after: today.toDate() });
    }

    return (
        <div className={className}>
            {label ? (
                <InputLabel htmlFor={id} className={labelClassName}>
                    {label}
                </InputLabel>
            ) : null}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        id={id}
                        data-testid={testId}
                        type='button'
                        variant='outline'
                        disabled={disabled}
                        className={classNames('w-full justify-between font-normal', styles.trigger)}
                        aria-label={label}
                    >
                        {value ? formatDisplay(value, format) : 'Select date'}
                        <CalendarIcon className='ml-2 h-4 w-4 opacity-70' />
                    </Button>
                </PopoverTrigger>
                <PopoverContent align='start' className='w-auto overflow-hidden p-0'>
                    <Calendar
                        mode='single'
                        captionLayout='dropdown'
                        selected={value ?? undefined}
                        // Opens on the month it is holding, not on today. Without this, editing
                        // a date years back means paging there every time it is opened.
                        defaultMonth={value ?? undefined}
                        onSelect={date => {
                            if (!date) {
                                onChange(null);
                                return;
                            }

                            const selected = moment(date).startOf('day');

                            if (minDate && selected.isBefore(minDate, 'day')) {
                                onChange(minDate.clone().startOf('day').toDate());
                                return;
                            }

                            if (disablePast && selected.isBefore(today, 'day')) {
                                onChange(today.clone().toDate());
                                return;
                            }

                            if (disableFuture && selected.isAfter(today, 'day')) {
                                onChange(today.clone().toDate());
                                return;
                            }

                            onChange(selected.toDate());
                            setOpen(false);
                        }}
                        disabled={disabledDays}
                    />
                </PopoverContent>
            </Popover>
        </div>
    );
};

export default BasicDatePicker;
